
import { getCachedOrFetch, getCachedOrPost } from './cache-management'
import { createRepository } from './base.repository'

export default createRepository(({ get, post }) => ({
  Initial: {
    get: ({ eventId = '', sportType = '', region = '', languageIdentifier = 'eng' }) => {
      const url = `?api=initial&sportType=${sportType}${region ? `&region=${region}` : ''}&eventId=${eventId}&languageIdentifier=${languageIdentifier}`
      return getCachedOrFetch('Initial', url, get)
    }
  },
  Statistics: {
    get: ({ eventId = '', sportType = '', region = '', languageIdentifier = 'eng' }) => {
      const url = `?api=statistics&sportType=${sportType}${region && `&region=${region}`}&eventId=${eventId}&languageIdentifier=${languageIdentifier}`
      return getCachedOrFetch('Statistics', url, get)
    }
  },
  Localization: {
    get: ({ languageIdentifier = 'eng', widgetId = '' }) => {
      const url = `?api=widget-ui-localization&widgetId=${widgetId}&languageIdentifier=${languageIdentifier}`
      return getCachedOrFetch('Localization', url, get)
    }
  },
  Standings: {
    get: ({ seasonId = '', groupId = '', competitionId = '', sportType = '', region = '', languageIdentifier = 'eng' }) => {
      const url = `?api=standings&seasonId=${seasonId}&groupId=${groupId}&competitionId=${competitionId}&sportType=${sportType}${
          region && `&region=${region}`
        }&languageIdentifier=${languageIdentifier}`
      return getCachedOrFetch('Standings', url, get)
    }
  },
  Leaderboard: {
    get: ({ seasonId = '', sportType = '', languageIdentifier = 'eng' }) => {
      const url = `?api=leaderboard&seasonId=${seasonId}&sportType=${sportType}&languageIdentifier=${languageIdentifier}`
      return getCachedOrFetch('Leaderboard', url, get)
    }
  },
  Fixtures: {
    get: ({ seasonId = '', startDate = '', endDate = '', round = '', roundIdentifier = '', sportType = '', region = '', languageIdentifier = 'eng' }) => {
      const dateQueries = startDate && endDate ? `&startDate=${startDate}&endDate=${endDate}` : ''
      const roundQueries = round ? `&round=${round}&roundLeagueId=${roundIdentifier}` : ''
      const url = `?api=fixtures&seasonId=${seasonId}${dateQueries}&sportType=${sportType}${region && `&region=${region}`}&languageIdentifier=${languageIdentifier}${roundQueries}`
      return getCachedOrFetch('Fixtures', url, get)
    }
  },
  PlayerProfile: {
    get: ({ playerId = '', sportType = '', region = '', languageIdentifier = 'eng', eventId = '' }) => {
      const url = `?api=profile&${eventId ? 'eventId=' + eventId + '&' : ''}sportType=${sportType}${
          region && `&region=${region}`
        }&playerId=${playerId}&languageIdentifier=${languageIdentifier}`
      return getCachedOrFetch('PlayerProfile', url, get)
    }
  },
  TeamProfile: {
    get: ({ competitorId = '', sportType = '', languageIdentifier = 'eng', region = '' }) => {
      const url = `?api=team-profile&sportType=${sportType}&competitorId=${competitorId}&languageIdentifier=${languageIdentifier}${region && `&region=${region}`}`
      return getCachedOrFetch('TeamProfile', url, get)
    }
  },
  Lineup: {
    get: ({ eventId = '', languageIdentifier = 'eng' }) => {
      const url = `?api=lineup&eventId=${eventId}&languageIdentifier=${languageIdentifier}`
      return getCachedOrFetch('Lineup', url, get)
    }
  },
  Scoreboard: {
    get: ({ competitorId = '', competitor2Id = '', sportType = '', region = '', languageIdentifier = 'eng' }) => {
      const url = `?api=scoreboard&competitorId=${competitorId}&competitor2Id=${competitor2Id}&languageIdentifier=${languageIdentifier}&sportType=${sportType}${
          region && `&region=${region}`
        }`
      return getCachedOrFetch('Scoreboard', url, get)
    }
  },
  Timeline: {
    get: ({ eventId = '', sportType = '', languageIdentifier = 'eng' }) => {
      const url = `?api=live-ticker&eventId=${eventId}&languageIdentifier=${languageIdentifier}&sportType=${sportType}`
      return getCachedOrFetch('Timeline', url, get)
    }
  },
  Boxscore: {
    get: ({ eventId = '', competitionId = '', region = '', sportType = '', languageIdentifier = 'eng' }) => {
      const url = `?api=box-score&eventId=${eventId}&competitionId=${competitionId}${region && `&region=${region}`}&sportType=${sportType}&languageIdentifier=${languageIdentifier}`
      return getCachedOrFetch('Boxscore', url, get)
    }
  },
  Roster: {
    get: ({ eventId = '', homeTeamId = '', awayTeamId = '', sportType = '', competitionId = '', region = '', languageIdentifier = 'eng' }) => {
      const url = `?api=roster&eventId=${eventId}&homeTeamId=${homeTeamId}&awayTeamId=${awayTeamId}${
          region && `&region=${region}`
        }&sportType=${sportType}&competitionId=${competitionId}&languageIdentifier=${languageIdentifier}`
      return getCachedOrFetch('Roster', url, get)
    }
  },
  Stripe: {
    get: ({ seasonIdList = '', date = '', sportType = '', languageIdentifier = 'eng', region = '' }) => {
      const url = `?api=stripe&seasonIdList=${seasonIdList}&date=${date}&sportType=${sportType}&languageIdentifier=${languageIdentifier}${region && `&region=${region}`}`
      return getCachedOrFetch('Stripe', url, get)
    }
  },
  StripeV2: {
    post: payload => {
      return getCachedOrPost('StripeV2', `?api=stripev2`, post, payload)
    }
  },
  Knockout: {
    get: ({ seasonId = '', sportType = '', region = '', languageIdentifier = 'eng' }) => {
      const url = `?api=knockout&seasonId=${seasonId}&sportType=${sportType}${region ? `&region=${region}` : ''}&languageIdentifier=${languageIdentifier}`
      return getCachedOrFetch('Knockout', url, get)
    }
  },
  TennisRankings: {
    get: ({ qualifier = '', limit = '50' }) => {
      const url = `?api=tennis/ranking/${qualifier}&limit=${limit}`
      return getCachedOrFetch('TennisRankings', url, get)
    }
  },
  Euroleague: {
    PlayerProfile: {
      get: ({ playerId = '', competitionCode = '' }) => {
        const competitionCodeParam = competitionCode ? `?competitionCode=${competitionCode}` : ''
        const url = `?api=basketball/euroleague/player-profile/${playerId}${competitionCodeParam}`
        return getCachedOrFetch('PlayerProfile', url, get)
      }
    },
    Roster: {
      get: ({ clubCode = '', seasonCode = '' }) => {
        const seasonCodeParam = seasonCode ? `&seasonCode=${seasonCode}` : ''
        const url = `?api=basketball/euroleague/roster&clubCode=${clubCode}${seasonCodeParam}`
        return getCachedOrFetch('Roster', url, get)
      }
    },
    MatchStats: {
      get: ({ eventId, seasonId }) => {
        const url = `?api=basketball/euroleague/match-stats&gameCode=${eventId}&seasonCode=${seasonId}`
        return getCachedOrFetch('MatchStats', url, get)
      }
    },
    Fixtures: {
      get: ({ seasonCode, round, dateFrom, dateTo }) => {
        let url = `?api=basketball/euroleague/fixtures&seasonCode=${seasonCode}`
        if (round) {
          url += `&round=${round}`
        } else {
          url += `&dateFrom=${dateFrom}&dateTo=${dateTo}`
        }
        return getCachedOrFetch('Fixtures', url, get)
      }
    },
    TeamProfile: {
      get: ({ clubCode = '', seasonCode = '', competitionCode = '' }) => {
        const competitionCodeParam = competitionCode ? `&competitionCode=${competitionCode}` : ''
        const url = `?api=basketball/euroleague/team-profile&clubCode=${clubCode}&seasonCode=${seasonCode}${competitionCodeParam}`
        return getCachedOrFetch('TeamProfile', url, get)
      }
    },
    PlayerLeaderboard: {
      get: ({ competitionCode = '', categories = [], seasonCode = '', limit = 10 }) => {
        const url = `?api=basketball/euroleague/player-leaders&competitionCode=${competitionCode}&categories=${categories}&seasonCode=${seasonCode}&limit=${limit}`
        return getCachedOrFetch('PlayerLeaderboard', url, get)
      }
    },
    Leaderboard: {
      get: ({ competitionCode = '', categories = [], seasonCode = '', limit = 10, leaderboardType = 'player' }) => {
        const url = `?api=basketball/euroleague/${leaderboardType}-leaders&competitionCode=${competitionCode}&categories=${categories}&seasonCode=${seasonCode}&limit=${limit}`
        return getCachedOrFetch('Leaderboard', url, get)
      }
    },
    PlayByPlay: {
      get: ({ competition = '', eventId = '', seasonId = '' }) => {
        const url = `?api=basketball/euroleague/play-by-play&competitionCode=${competition}&seasonCode=${seasonId}&gameCode=${eventId}`
        return getCachedOrFetch('PlayByPlay', url, get)
      }
    }
  },
  LiveScoreboard: {
    get: ({ sportType = '', timelineTypes = [], sortDirection = 'desc', eventId = '', languageIdentifier = 'eng' }) => {
      const url = `?api=live-scoreboard&sportType=${sportType}&timelineTypes=${timelineTypes}&sortDirection=${sortDirection}&eventId=${eventId}&language=${languageIdentifier}`
      return getCachedOrFetch('LiveScoreboard', url, get)
    }
  },
  TennisPlayerProfile: {
    get: ({ playerId = '' }) => {
      const url = `?api=tennis/profile/${playerId}`
      return getCachedOrFetch('TennisPlayerProfile', url, get)
    }
  },
  TennisStatistics: {
    get: ({ eventId = '' }) => {
      const url = `?api=tennis/match-stats/${eventId}`
      return getCachedOrFetch('TennisStatistics', url, get)
    }
  },
  TennisDraw: {
    get: ({ seasonId = '' }) => {
      const url = `?api=tennis/draw/${seasonId}`
      return getCachedOrFetch('TennisDraw', url, get)
    }
  },
  TennisFixtures: {
    get: ({ seasonId, round, startDate, endDate }) => {
      let url = `?api=tennis/fixtures/${seasonId}`
      if (round) {
        url += `&round=${round}`
      } else {
        url += `&dateFrom=${startDate}&dateTo=${endDate}`
      }
      return getCachedOrFetch('TennisFixtures', url, get)
    }
  },
  FootballTeamProfile: {
    get: ({ teamId = '' }) => {
      const url = `?api=football/team-profile/${teamId}`
      return getCachedOrFetch('FootballTeamProfile', url, get)
    }
  }
}))